<template>
    <slot name="row" v-bind="props">
        <tr :id="name + '-' + computedIdentifier" class="row border-none group relative hover:bg-gray-tier-5" :class="computedRowClasses" v-bind="$attrs" @click="showSubRows = !showSubRows">
            <td v-if="selectable !== false">
                <SeclockInput
                    v-if="runWhenClosure(selectable)"
                    v-model="computedSelected"
                    type="checkbox"
                    label=""
                />
            </td>
            <td v-for="(column, key) in columns"
                class="relative group first-of-type:!pl-6 !px-2.5 py-2 at1024:py-[14px]"
                :class="cellClasses(column)"
            >
                <div v-if="depth && key === 0" :class="['pl-' + (depth * 4)]" class="float-left h-2.5 mr-2 mb-4 mt-[5px] border-l-2 border-b-2 border-gray-tier-7"></div>
                <span
                    v-if="!cellComponent(column)"
                    v-html="cellContent(column)"
                />
                <component
                    v-else
                    :is="cellComponent(column)"
                    v-bind="cellComponentProps(column)"
                />
            </td>
        </tr>
    </slot>

    <slot name="subRows" v-bind="props" v-if="computedSubRows && (!collapsable || showSubRows)">
        <SubRow
            v-for="(row, key) in computedSubRows"
            v-bind="subRowProps(row)"
            @update:selected="(value)=>$emit('update:selected',value)"
        />
    </slot>

<!--    <slot name="subTable" v-bind="props" v-if="computedSubTable  && (!collapsable || showSubRows)">-->
<!--        <TableNew v-bind="computedSubTable" @update:selected="(value)=>$emit('update:selected',value)"/>-->
<!--    </slot>-->

</template>

<script setup>
    import _ from 'lodash';
    import { ref, defineAsyncComponent, defineOptions, computed } from 'vue';
    import { SeclockInput } from '@/Components';

    defineOptions({
        inheritAttrs: false,
    })

    const props = defineProps({
        "name": {
            type: String,
            required: true,
        },
        "columns": {
            type: Array,
            required: true,
        },
        "row": {
            type: Object,
            required: true,
        },
        "rowClasses": {
            type: [String, Function],
            required: false,
            default: '',
        },
        "rowIdentifier": {
            type: [String,Function],
            required: true,
            default: 'id',
        },
        "subRowPath": {
            type: [String, Function],
            required: false,
            default: false,
        },
        "collapsable": {
            type: Boolean,
            required: false,
            default: false,
        },
        "depth": {
            type: Number,
            required: false,
            default: 0,
        },
        "selectable": {
            type: [Boolean, Function],
            required: false,
            default: false,
        },
        "selected": {
            type: Object,
            required: false,
            default: {},
        },
    });

    const emit = defineEmits(['update:selected']);

    const showSubRows = ref(true);

    function valueOfPath(source, key, placeholder = '-') {
        return _.get(source, key, placeholder);
    }

    function runWhenClosure(closure, params = {}) {
        return _.isFunction(closure) ? closure({row:props.row,props:props.value, ...params}) : closure;
    }

    function subRowProps(row) {
        return {
            ...props,
            row,
            depth: props.depth + 1,
        };
    }

    function cellClasses(column){
        return runWhenClosure(props.columns.cellClass, {column});
    }

    const computedIdentifier = computed(() => {
        return _.isFunction(props.rowIdentifier) ? props.rowIdentifier({row:props.row,props:props.value}) : valueOfPath(props.row,props.rowIdentifier);
    });

    const computedSubRows = computed(() => !props.subRowPath ? false : _.get(props.row, props.subRowPath, []));

    const computedSubTable = computed(() => {
        return !props.subTable ? false : {
            ...props.subTable,
            rows: _.get(props.row, props.subTable.path, []),
        };
    });

    const computedSelected = computed({
        get: () => _.get(props.selected,computedIdentifier.value,false),
        set: () => {
            let selected = props.selected;
            selected[computedIdentifier.value] = value;
            emit('update:selected', selected);
        }
    });

    function cellContent(column){
        return _.isFunction(column.path)
            ? runWhenClosure(column.path)
            : valueOfPath(props.row, column.path, column.placeholder)
    }

    function cellComponent(column){
        return runWhenClosure(column.component);
    }

    function cellComponentProps(column) {
        return runWhenClosure(column.props);
    }

    const computedRowClasses = computed(() => runWhenClosure(props.rowClasses));

    const SubRow = defineAsyncComponent(() => import("./TableRowThree.vue"));

</script>
